import { useCallback, useEffect, useMemo, useState } from "react";
import { CaretRight, DownloadSimple, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { RECON_TONE, fpBase, inr, shortInr, useFpContext } from "@/lib/fp";

const GROUP_TONE = {
  Revenue: "#C2410C",
  "Production variable cost": "#0F766E",
  "Distribution variable cost": "#7C3AED",
  "Fixed cost": "#1F3B57",
  Wastage: "#B45309",
  Summary: "#0369A1",
};

const Leg = ({ leg }) => (
  <tr className="border-t border-border/70">
    <td className="py-2 pr-3 text-navy">{leg.label}</td>
    <td className="pr-3 text-[11px] text-muted-foreground">{leg.detail}</td>
    <td className="pr-3 text-[11px] text-muted-foreground">{leg.source}</td>
    <td className="w-6 text-center font-mono">{leg.sign > 0 ? "+" : "−"}</td>
    <td className="text-right font-mono tabular-nums">{inr(leg.amount)}</td>
  </tr>
);

const TraceRow = ({ row, open, onToggle }) => (
  <div className="overflow-hidden rounded-2xl border border-border bg-card"
       data-testid={`fp-trace-row-${row.id}`}>
    <button onClick={onToggle} data-testid={`fp-trace-toggle-${row.id}`}
            className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60">
      <CaretRight size={14}
                  className={`shrink-0 text-terracotta transition-transform ${open ? "rotate-90" : ""}`} />
      <span className="h-8 w-[3px] shrink-0 rounded-full"
            style={{ background: GROUP_TONE[row.group] || "#1F3B57" }} />
      <span className="min-w-0 flex-1">
        <span className="label-caps block">{row.group}</span>
        <span className="block truncate text-sm text-navy">
          {row.context} · {row.figure}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block whitespace-nowrap font-display text-lg tabular-nums text-navy">
          {shortInr(row.result)}
        </span>
        <span className="block whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">
          {inr(row.result)}
        </span>
      </span>
    </button>
    {open && (
      <div className="border-t border-border bg-muted/30 px-5 py-4" data-testid={`fp-trace-legs-${row.id}`}>
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
          {row.formula}
        </p>
        <table className="mt-3 w-full text-xs">
          <tbody>
            {row.legs.map((leg, i) => <Leg key={i} leg={leg} />)}
            <tr className="border-t-2 border-navy/30">
              <td className="py-2 font-semibold text-navy" colSpan={4}>Result</td>
              <td className="text-right font-mono font-semibold tabular-nums text-navy">
                {inr(row.result)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function FpTrace() {
  const { tenantId, period, basis } = useFpContext();
  const { isPlatformAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: res } = await api.get(`${fpBase(tenantId)}/trace`,
                                          { params: { period, basis } });
      setData(res);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = Object.values(data?.groups || {}).flat();
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => `${r.group} ${r.figure} ${r.context}`.toLowerCase().includes(q)
      || (r.legs || []).some((l) => `${l.label} ${l.detail}`.toLowerCase().includes(q)));
  }, [data, query]);

  const base = `${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}`;

  return (
    <FpShell
      title="Trace & exceptions"
      description="Every figure the engine publishes, with the arithmetic behind it and the client file each leg came from. Open a line to see how it was arrived at."
      actions={(isPlatformAdmin || data?.can_download) && (
        <a href={`${base}/audit-pack.xlsx?period=${period}&basis=${basis}`}
           data-testid="fp-trace-download"
           className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
          <DownloadSimple size={13} /> Download audit pack
        </a>
      )}
    >
      {!data && <p className="label-caps animate-pulse">Building the audit trail</p>}
      {data?.error && <p className="panel p-6 text-sm text-navy">{data.error}</p>}

      {data && !data.error && (
        <div className="space-y-6" data-testid="fp-trace">
          <div className="panel p-6">
            <p className="label-caps">What the engine publishes for this month</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {data.summary.map((s) => (
                <div key={s.line} data-testid={`fp-trace-summary-${s.line.toLowerCase().replace(/\W+/g, "-")}`}>
                  <p className="label-caps">{s.line}</p>
                  <p className="mt-1 whitespace-nowrap font-display text-xl tabular-nums text-navy">
                    {shortInr(s.value)}
                  </p>
                  <p className="whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">
                    {inr(s.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <MagnifyingGlass size={14}
                             className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
                   data-testid="fp-trace-search"
                   placeholder="Search a figure, outlet, category, department or ledger"
                   className="w-full rounded-full border border-border bg-card py-3 pl-11 pr-4 text-sm text-navy outline-none focus:border-terracotta" />
          </div>

          <div className="space-y-3">
            {rows.map((row) => (
              <TraceRow key={row.id} row={row} open={open === row.id}
                        onToggle={() => setOpen(open === row.id ? null : row.id)} />
            ))}
            {!rows.length && (
              <p className="panel p-6 text-sm text-muted-foreground">
                Nothing matches “{query}”.
              </p>
            )}
          </div>

          <div className="panel overflow-hidden" data-testid="fp-trace-control-detail">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Both sides of every figure</p>
              <h3 className="font-display text-lg text-navy">
                Tally control total vs interface detail total
              </h3>
              <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
                The control total is what the client's Tally books hold for the item. The detail
                total is the same figure rebuilt from the supplementary sheets that carry the
                outlet, category and cost-component splits. The file each side was read from is
                named underneath. Expected output packs are never used on either side.
              </p>
            </div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="label-caps text-left">
                    <th className="px-5 py-2">Control item</th>
                    <th className="px-5 py-2 text-right">Tally control total</th>
                    <th className="px-5 py-2 text-right">Interface detail total</th>
                    <th className="px-5 py-2 text-right">Difference</th>
                    <th className="px-5 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.reconciliation || []).map((r) => (
                    <tr key={r.control_item} className="border-t border-border align-top"
                        data-testid={`fp-trace-pair-${r.control_item.toLowerCase().replace(/\W+/g, "-")}`}>
                      <td className="px-5 py-2 font-medium text-navy">{r.control_item}</td>
                      <td className="px-5 py-2 text-right">
                        <span className="font-mono tabular-nums text-navy">
                          {r.control_total === null ? "—" : inr(r.control_total)}
                        </span>
                        {r.control_documents?.length > 0 && (
                          <p className="truncate text-[9px] text-muted-foreground/70"
                             title={r.control_documents.join(", ")}>
                            {r.control_documents.join(", ")}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-2 text-right">
                        <span className="font-mono tabular-nums text-foreground/80">
                          {r.detail_total === null ? "—" : inr(r.detail_total)}
                        </span>
                        {r.detail_documents?.length > 0 && (
                          <p className="truncate text-[9px] text-muted-foreground/70"
                             title={r.detail_documents.join(", ")}>
                            {r.detail_documents.join(", ")}
                          </p>
                        )}
                      </td>
                      <td className={`px-5 py-2 text-right font-mono tabular-nums ${
                        r.difference ? "font-semibold text-destructive" : "text-success"}`}>
                        {r.difference === null ? "—" : inr(r.difference)}
                      </td>
                      <td className="px-5 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${RECON_TONE[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!(data.reconciliation || []).length && (
                    <tr><td colSpan={5} className="px-5 py-4 text-muted-foreground">
                      No reconciliation for this month yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel p-6" data-testid="fp-trace-exceptions">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="label-caps">Nothing here has been absorbed into a number</p>
                <h3 className="flex items-center gap-2 font-display text-lg text-navy">
                  <Warning size={18} className="text-[hsl(30_88%_38%)]" /> Exceptions
                </h3>
              </div>
              <div className="text-right">
                <p className="label-caps">Unplaced value</p>
                <p className="font-mono text-lg font-semibold tabular-nums text-[hsl(30_88%_28%)]"
                   data-testid="fp-trace-exception-total">{inr(data.exception_total)}</p>
              </div>
            </div>
            <div className="mt-4 max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="label-caps text-left">
                    <th className="py-2">Kind</th><th>Name</th><th>Context</th>
                    <th className="text-right">Amount</th><th>Why it is here</th>
                  </tr>
                </thead>
                <tbody>
                  {data.exceptions.map((e, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`fp-trace-exception-${i}`}>
                      <td className="py-2 text-navy">{e.kind}</td>
                      <td>{e.name}</td>
                      <td className="text-muted-foreground">{e.context || "—"}</td>
                      <td className="text-right font-mono tabular-nums">{inr(e.amount)}</td>
                      <td className="text-muted-foreground">{e.issue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </FpShell>
  );
}
